/**
 * Shared asset-path predicate, used by both the loaders (subasset re-homing) and
 * the export pipeline (asset collection). Kept in `utils/` — a neutral location —
 * so `loaders/` and `export/` share one definition instead of hand-syncing copies.
 */

/**
 * Detect a bundled local asset path (`./assets/foo.png` or `assets/foo.png`).
 *
 * Prefix-based: a (hypothetical) string field whose value happens to start with
 * `assets/` but isn't an asset would match. No current field does this — keep
 * asset paths under the `assets/` prefix and arbitrary text out of it.
 */
export function isLocalAssetPath(value: unknown): value is string {
    return typeof value === 'string'
        && (value.startsWith('./assets/') || value.startsWith('assets/'));
}

/**
 * Detect a site-absolute asset path (`/generated/<hash>.jpg`, `/components/…`) —
 * an asset addressed relative to whichever origin serves the document.
 *
 * These are the paths the authoring server emits for its own libraries and for
 * generated media. They are **origin-dependent**, which is what separates them
 * from both bundled assets and `http(s)://` URLs: nothing outside the authoring
 * origin can resolve one. So export must always bundle them (they can never be
 * "preserved as an external reference" the way a CDN URL can), and the loader
 * must fetch them from the origin rather than look for a registered blob.
 *
 * Protocol-relative URLs (`//host/path`) are deliberately excluded — they carry
 * their own host and behave like `http(s)://` URLs, not like origin paths.
 *
 * Returns a plain boolean rather than a `value is string` guard on purpose:
 * every call site already holds a `string`, and narrowing one by `is string`
 * collapses the *negative* branch to `never` — which turns the subsequent
 * `${declaredSrc}` log into a type error.
 */
export function isSiteAbsolutePath(value: unknown): boolean {
    return typeof value === 'string'
        && value.startsWith('/')
        && !value.startsWith('//');
}
